import React, { useState, useEffect } from 'react';
import { 
  Box, Card, CardContent, Typography, TextField, 
  Grid, Button, CircularProgress, Alert, 
  Accordion, AccordionSummary, AccordionDetails, 
  Chip, Divider, Collapse
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  LocalShipping as TruckIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  BugReport as DebugIcon,
  Restore as RestoreIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { questionnaireService } from '../../services/questionnaireService';
import { MasterState, ScopeAndMaterialPayload } from '../../types/scopeAndMaterial';

// --- UTILS ---
const formatLabel = (key: string) => {
    if (!key) return '';
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const safeRender = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') return value.description || value.name || value.value || JSON.stringify(value);
  return String(value);
};

const getRooms = (masterState: MasterState | null): any[] => {
  if (!masterState || !masterState.room_schedule) return [];
  const schedule = masterState.room_schedule;
  if (Array.isArray(schedule)) return schedule;
  if (typeof schedule === 'object') {
    const values = Object.values(schedule);
    if (values.length > 0 && typeof values[0] === 'object') return values;
    return [schedule];
  }
  return [];
};

// --- EDITABLE FIELD COMPONENT ---
// Updated to accept isReadOnly
const EditableField: React.FC<{ label: string; value: any; onUpdate: (val: any) => void; isReadOnly?: boolean; }> = ({ label, value, onUpdate, isReadOnly = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  useEffect(() => { setTempValue(value); }, [value]);

  const handleStartEditing = () => {
    if (!isReadOnly) {
      setIsEditing(true);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">{formatLabel(label)}</Typography>
      {isEditing && !isReadOnly ? (
        <TextField
          fullWidth size="small" variant="outlined" value={typeof tempValue === 'object' ? JSON.stringify(tempValue) : tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={() => { onUpdate(tempValue); setIsEditing(false); }}
          autoFocus multiline sx={{ mt: 0.5 }}
        />
      ) : (
        <Box 
          onClick={handleStartEditing} 
          sx={{ 
            p: 1, 
            mt: 0.5, 
            borderRadius: 1, 
            border: '1px dashed transparent', 
            '&:hover': !isReadOnly ? { bgcolor: 'action.hover', border: '1px dashed #ccc', cursor: 'pointer' } : {}, 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between',
            opacity: isReadOnly ? 0.8 : 1
          }}
        >
          <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{safeRender(value) || 'Not specified'}</Typography>
          {!isReadOnly && <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5, mt: 0.5, ml: 1 }} />}
        </Box>
      )}
    </Box>
  );
};

// --- MAIN COMPONENT ---
// Updated to accept isReadOnly
const ScopeSummary: React.FC<{ projectId: string; isReadOnly?: boolean }> = ({ projectId, isReadOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [originalAiState, setOriginalAiState] = useState<MasterState | null>(null);
  const [activeState, setActiveState] = useState<MasterState | null>(null);
  
  const [showDebug, setShowDebug] = useState(false);
  
  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      const data: ScopeAndMaterialPayload = await questionnaireService.getScopeAndMaterials(projectId);
      
      if (data) {
        setOriginalAiState(data.master_state); 
        
        // FIX: Check for existence, not length (keys)
        if (data.verified_master_state !== undefined && data.verified_master_state !== null) {
          setActiveState(data.verified_master_state);
        } else {
          setActiveState(JSON.parse(JSON.stringify(data.master_state)));
        }
      }
    } catch (error) {
      console.error("Failed to load scope", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (section: string, key: string, value: any, index?: number) => {
    if (!activeState || isReadOnly) return;
    const newState = JSON.parse(JSON.stringify(activeState));
    
    if (index !== undefined) {
      if (Array.isArray(newState[section])) {
        newState[section][index][key] = value;
      } else {
        const keys = Object.keys(newState[section]);
        if (keys.includes(String(index))) newState[section][index][key] = value;
        else newState[section][key] = value;
      }
    } else if (typeof newState[section] === 'object') {
      newState[section][key] = value;
    }
    setActiveState(newState);
  };

  const handleSave = async () => {
    if (!activeState || isReadOnly) return;
    setSaving(true);
    try {
      // OPTIMIZED: Send only the verified state.
      await questionnaireService.saveScopeAndMaterials(projectId, { 
        verified_master_state: activeState 
      });
      alert("Scope verified and saved!");
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save scope.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (isReadOnly) return;
    if (window.confirm("This will discard all your edits and revert to the original AI suggestions. Are you sure?")) {
      setActiveState(JSON.parse(JSON.stringify(originalAiState)));
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!activeState) return <Alert severity="error">No data found.</Alert>;

  const rooms = getRooms(activeState);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Scope Verification {isReadOnly && <Chip size="small" label="Locked" color="default" icon={<LockIcon />} sx={{ ml: 1 }} />}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isReadOnly 
              ? "This scope has been finalized and cannot be edited."
              : "Review and edit the project details. This will form the basis of your contract."}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="warning"
            startIcon={<RestoreIcon />} 
            onClick={handleReset}
            disabled={saving || isReadOnly}
          >
            Reset to AI Default
          </Button>
          <Button 
            variant="contained" 
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || isReadOnly}
          >
            {saving ? 'Saving...' : 'Confirm Scope'}
          </Button>
        </Box>
      </Box>

      {/* 1. PROJECT ARCHETYPE & LOGISTICS */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HomeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Project Overview</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {activeState.project_archetype && Object.entries(activeState.project_archetype).map(([key, value]) => (
                <EditableField key={key} label={key} value={value} onUpdate={(val) => handleUpdate('project_archetype', key, val)} isReadOnly={isReadOnly} />
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TruckIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Logistics & Access</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {activeState.logistics && Object.entries(activeState.logistics).map(([key, value]) => (
                <EditableField key={key} label={key} value={value} onUpdate={(val) => handleUpdate('logistics', key, val)} isReadOnly={isReadOnly} />
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 2. ROOM SCHEDULE */}
      <Typography variant="h6" sx={{ mb: 2 }}>Room Schedule</Typography>
      {rooms.length > 0 ? (
        rooms.map((room: any, index: number) => {
          if (typeof room !== 'object' || room === null) return null;
          const roomName = room.name || room.room_name || room.title || `Room ${index + 1}`;
          const isWetRoom = room.is_wet_room === true || String(roomName).toLowerCase().includes('bath') || String(roomName).toLowerCase().includes('kitchen');

          return (
            <Accordion key={index} defaultExpanded={true} sx={{ mb: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" fontWeight="bold">{roomName}</Typography>
                {isWetRoom && <Chip label="Wet Room" size="small" color="info" sx={{ ml: 2 }} />}
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {Object.entries(room).map(([key, value]) => {
                    if (key === 'name' || key === 'room_id' || key === 'is_wet_room') return null;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <EditableField label={key} value={value} onUpdate={(val) => handleUpdate('room_schedule', key, val, index)} isReadOnly={isReadOnly} />
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })
      ) : (
        <Alert severity="warning" sx={{ mb: 3 }}>No room schedule generated yet.</Alert>
      )}

      {/* 3. GLOBAL CONSTRAINTS & EXCLUSIONS */}
      <Accordion sx={{ mb: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Global Constraints & Exclusions</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Constraints</Typography>
              {activeState.global_constraints && Object.entries(activeState.global_constraints).map(([key, value]) => (
                <EditableField key={key} label={key} value={value} onUpdate={(val) => handleUpdate('global_constraints', key, val)} isReadOnly={isReadOnly} />
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Exclusions</Typography>
              {activeState.exclusions && Array.isArray(activeState.exclusions) && (
                <Box component="ul" sx={{ pl: 2 }}>
                  {activeState.exclusions.map((item: any, i: number) => (
                    <li key={i}><Typography variant="body2">{safeRender(item)}</Typography></li>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ScopeSummary;